import Delta from "quill-delta";
import { canv } from "@/canv/global";
import { getContrastColor, lightOrDark } from "@/canv/utils";
import { ShapeWidget } from "@/elements/shape";
import { StickerWidget } from "@/elements/sticker";
import { TableWidget } from "@/elements/table/table";
import { selectionManager } from "@/tools/cursorTool";
import { TEXT_AUTOSAVE_DELAY_MS } from "./constantsV2";
import { QuillEditor } from "./quillEditor";
import { TextWidgetV2 } from "./textV2";
import type {
    LimitNoticeKind,
    QuillFormats,
    ResizeResult,
    SelectionSummary,
    TextChangeMeta,
} from "./typesV2";
import { enforceDeltaColor, isUIElement, sanitizeDeltaAlign, sceneToClient } from "./utilsV2";

export class TextEditingController {
    private textWidget: TextWidgetV2 | null = null;
    private quillEditor: QuillEditor;
    private removeClickOutside: (() => void) | null = null;
    private removeEditorLinkListeners: (() => void) | null = null;
    private removeEditorContextMenu: (() => void) | null = null;
    private lastOverlayTransformSignature: string | null = null;
    private autosaveTimer: number | null = null;
    private lastPersistedEditingStateKey: string | null = null;
    private forcedTextColor: string | null = null;

    constructor() {
        const overlayRoot = document.querySelector("#canvas-overlay-root") as HTMLDivElement;

        this.quillEditor = new QuillEditor({
            overlayRoot,
            onSize: this.onEditorTextChange,
            onSelectionChange: this.onEditorSelectionChange,
            onTextChange: this.onEditorContentChange,
            onFontSizeChange: this.onFontSizeChange,
            onPasteApplied: () => this.applyPostPasteFormatting(),
            getCurrentTextData: this.getCurrentTextData,
        });
    }

    getIsEditing() {
        return this.textWidget !== null;
    }

    isEditingWidget(textWidget: TextWidgetV2) {
        return this.textWidget?.UID === textWidget.UID;
    }

    startEdit(textWidget: TextWidgetV2, focusAtPointer = false) {
        if (this.textWidget?.UID === textWidget.UID) {
            return;
        }

        if (this.textWidget) {
            this.commit();
        }
        // важно самому сбрасывать таймер долгого выделения, так как mousedown всегда идет по канвасу actionManager его ловит,
        // а mouseup уже происходит по текстовому блоку редактора (isTextEditorPointerEvent) из-за чего mouseup никогда не долетает до CursorTool
        // mousedown на canvas -> тригер долгого выделения, mouseup не по canvas -> через 3 секунды сработает долгое выделение
        canv.toolBox.TryGetToolByName("cursorTool").finishTimerForLongSelect(true);

        this.textWidget = textWidget;
        this.forcedTextColor = this.resolveForcedTextColor(textWidget);
        this.lastOverlayTransformSignature = null;
        this.lastPersistedEditingStateKey = this.buildEditingStateKey(
            this.textWidget.getData().delta,
        );

        // Для пустого стикера заранее считаем fit до открытия редактора,
        // чтобы каретка сразу была в том же масштабе, что и будущий текст.
        if (
            this.textWidget.resizeMode === "sticker" &&
            this.textWidget.symbolsCount === 0 &&
            this.textWidget.hardWidth > 0 &&
            this.textWidget.hardHeight > 0
        ) {
            this.textWidget.fitSticker(true);
            this.textWidget.updateDims();
        }

        const domLineHeightPx = this.textWidget.lineHeight;

        const { fontSize, fontFamily, textAlign, delta } = this.textWidget.getData();

        this.quillEditor.open(
            {
                fontSize,
                lineHeight: domLineHeightPx,
                fontFamily,
                textAlign,
                color: this.textWidget.getCurrentColor(),
                horizontalPadding: this.textWidget.horizontalPadding,
            },
            delta,
        );

        if (this.textWidget.resizeMode === "shape" || this.textWidget.resizeMode === "sticker") {
            this.quillEditor.setResizeOptions({
                mode: this.textWidget.resizeMode,
                emptyWidth: this.textWidget.getEmptyInputWidth(),
                paddingX: this.textWidget.horizontalPadding,
                fixedWidth: this.textWidget.hardWidth,
                fixedHeight: this.textWidget.hardHeight,
                currentFontSize: fontSize,
            });
        } else {
            this.quillEditor.setResizeOptions({
                mode: this.textWidget.resizeMode,
                emptyWidth: this.textWidget.getEmptyInputWidth(),
                paddingX: this.textWidget.horizontalPadding,
                onlyLineMode: this.textWidget.onlyLineMode,
            });
        }

        this.bindOverlayEvents();
        this.updateOverlayTransform();
        this.applyFocusMode(focusAtPointer);
        canv.appInterface.linkPopupController.attachEditorSession(
            this.textWidget.UID,
            this.quillEditor.getQuill(),
        );
        canv.appInterface.linkPopupController.onEditorSelectionChange();

        if (this.forcedTextColor) {
            this.quillEditor.setForcedColor(this.forcedTextColor);
        } else if (this.textWidget.symbolsCount === 0) {
            // Для пустого текста явно задаем активный цвет в Quill после выставления фокуса/каретки,
            // чтобы первый ввод сохранялся с текущим цветом.
            this.quillEditor.applyTextFormatting("color", this.textWidget.getCurrentColor());
        }

        canv.appInterface.inputFocused = 1;

        this.installClickOutside();
        this.installEditorLinkListeners();
        this.installEditorContextMenu();
    }

    commit() {
        if (!this.textWidget) {
            return;
        }

        if (this.textWidget.resizeMode === "sticker") {
            this.quillEditor.flushStickerFit();
        }

        const shouldApplyCommittedDelta =
            this.quillEditor.getIsTextChanged() || this.hasUnsavedEditingState();

        this.flushAutosave();

        if (shouldApplyCommittedDelta) {
            this.textWidget.aiGenerated = false;
            this.textWidget.setNewDelta(this.normalizeEditingDelta(this.quillEditor.getDelta()));
        }

        this.cleanup();
    }

    cancel() {
        if (!this.textWidget) {
            return;
        }

        this.clearAutosaveTimer();
        this.cleanup();
    }

    updateOverlayTransform(newWidth = 0) {
        if (!this.textWidget) {
            return;
        }

        this.syncResizeOptionsFromWidget();

        const zoom = canv.m[0] / canv.dpr;

        // World координаты
        const worldX = this.textWidget.startX;
        const worldY = this.textWidget.startY - this.textWidget.lineHeight;

        // Viewport координаты
        const { clientX, clientY } = sceneToClient({ x: worldX, y: worldY }, canv.m, canv.dpr);

        const width = newWidth || this.textWidget.width;
        const height = this.textWidget.height;

        // Компенсация transform-origin: center center
        const xAdjusted = clientX + (width * (zoom - 1)) / 2;
        const yAdjusted = clientY + (height * (zoom - 1)) / 2;

        const rotation = canv.canvasController.selected.length
            ? -canv.canvasController.selected[0].angle
            : 0;

        const { fontSize, fontFamily, textAlign } = this.textWidget.getData();
        const nextSignature = [
            xAdjusted,
            yAdjusted,
            width,
            height,
            zoom,
            fontSize,
            this.textWidget.lineHeight,
            fontFamily,
            this.textWidget.horizontalPadding,
            textAlign,
            rotation,
        ].join("|");

        if (this.lastOverlayTransformSignature === nextSignature) {
            return;
        }
        this.lastOverlayTransformSignature = nextSignature;

        this.quillEditor.updateTransform({
            x: xAdjusted,
            y: yAdjusted,
            width,
            height,
            scale: zoom,
            fontSize,
            lineHeight: this.textWidget.lineHeight,
            fontFamily,
            horizontalPadding: this.textWidget.horizontalPadding,
            textAlign,
            rotation,
        });

        canv.appInterface.linkPopupController.onViewportTransformChange();
    }

    syncAfterHostResize() {
        if (!this.textWidget) {
            return;
        }

        this.syncResizeOptionsFromWidget();
        this.quillEditor.remeasure();
        if (this.textWidget.resizeMode === "sticker") {
            this.quillEditor.flushStickerFit();
            this.alignStickerTextToHost();
        }
        this.updateOverlayTransform();
    }

    applyTextFormatting(name: QuillFormats, value: string | boolean) {
        this.quillEditor.applyTextFormatting(name, value);
    }

    applyTextFormattingToAll(name: QuillFormats, value: string | boolean) {
        this.quillEditor.applyTextFormattingToAll(name, value);
    }

    setForcedTextColor(color: string | null) {
        this.forcedTextColor = color;

        if (!this.textWidget) {
            return;
        }

        this.quillEditor.setForcedColor(color);

        if (color) {
            this.quillEditor.applyTextFormattingToAll("color", color);
        }
    }

    insertText(text: string, replaceAll = false) {
        if (!this.textWidget || !text) {
            return;
        }

        const quill = this.quillEditor.getQuill();
        this.quillEditor.focus();

        if (replaceAll) {
            const fullLength = Math.max(0, quill.getLength() - 1);
            quill.setSelection(0, fullLength, "silent");
        }

        this.quillEditor.replaceSelectionWithText(text);
    }

    insertDelta(delta: Delta, replaceAll = false, limitKind: LimitNoticeKind = "typing") {
        if (!this.textWidget) {
            return;
        }

        const quill = this.quillEditor.getQuill();
        this.quillEditor.focus();

        if (replaceAll) {
            const fullLength = Math.max(0, quill.getLength() - 1);
            quill.setSelection(0, fullLength, "silent");
        }

        this.quillEditor.replaceSelectionWithDelta(delta, limitKind);
    }

    insertHtml(html: string, replaceAll = false, limitKind: LimitNoticeKind = "paste") {
        const delta = this.convertPasteHtmlToDelta(html);
        const sanitizedDelta = sanitizeDeltaAlign(delta);

        this.insertDelta(sanitizedDelta, replaceAll, limitKind);
        this.applyPostPasteFormatting();
    }

    insertPastedText(text: string, replaceAll = false) {
        if (!text) {
            return;
        }

        this.insertDelta(new Delta().insert(text), replaceAll, "paste");
        this.applyPostPasteFormatting();
    }

    async pasteFromClipboard(replaceAll = false): Promise<void> {
        if (!this.textWidget) {
            return;
        }

        const quill = this.quillEditor.getQuill();
        this.quillEditor.focus();

        if (replaceAll) {
            const fullLength = Math.max(0, quill.getLength() - 1);
            quill.setSelection(0, fullLength, "silent");
        }

        await this.quillEditor.pasteFromClipboard();
        this.applyPostPasteFormatting();
    }

    patchDelta(name: QuillFormats, value: string | boolean, delta: Delta) {
        return this.quillEditor.patchDelta(name, value, delta);
    }

    getSelection() {
        return this.quillEditor.getSelection();
    }

    selectAll() {
        if (!this.textWidget) {
            return;
        }

        this.quillEditor.selectAll();
    }

    convertPasteHtmlToDelta(html: string): Delta {
        return this.quillEditor.convertHtmlToDeltaForPaste(html);
    }

    async handleContextMenuEditCommand(command: "copy" | "paste" | "delete"): Promise<void> {
        if (!this.textWidget) {
            return;
        }

        this.quillEditor.focus();

        switch (command) {
            case "copy": {
                await this.quillEditor.copySelectionToClipboard();
                return;
            }
            case "paste": {
                await this.pasteFromClipboard();
                return;
            }
            case "delete": {
                this.quillEditor.deleteSelectionOrBackward();
                return;
            }
        }
    }

    showSelectionRects() {
        const scale = canv.m[0] / canv.dpr;

        this.quillEditor.showSelectionRects(scale);
    }

    hideSelectionRects() {
        this.quillEditor.hideSelectionRects();
    }

    private bindOverlayEvents() {
        this.quillEditor.onKeyDownCapture = (event) => {
            if (!this.textWidget?.onlyLineMode || event.key !== "Enter") {
                return;
            }

            if (event.ctrlKey || event.metaKey) {
                return;
            }

            if (event.isComposing || event.keyCode === 229) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();
        };

        this.quillEditor.onKeyDown = (event) => {
            if (canv.contextMenu.isOpened) {
                canv.contextMenu.hide();
            }

            event.stopPropagation();

            if (this.handleStickerOrderedListAutofill(event)) {
                return;
            }

            if (event.key === "Escape") {
                event.preventDefault();
                this.commit();
                return;
            }

            if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
                event.preventDefault();
                this.commit();
                return;
            }

            // важно прокидывать вверх событие с shift, чтобы работало выделение нескольких ячеек
            if (event.code === "ShiftLeft" || event.code === "ShiftRight") {
                document.dispatchEvent(
                    new KeyboardEvent("keydown", {
                        key: event.key,
                        code: event.code,
                        shiftKey: event.shiftKey,
                        bubbles: true,
                    }),
                );
            }
        };
    }

    // Quill list-autofill (1. + Space) опирается на prefix текущего leaf.
    // В стикере строка может быть разбита на несколько leaf из-за форматирования,
    // поэтому штатный autofill срабатывает нестабильно. Здесь проверяем префикс всей строки.
    private handleStickerOrderedListAutofill(event: KeyboardEvent): boolean {
        if (!this.textWidget || this.textWidget.resizeMode !== "sticker") {
            return false;
        }

        if (event.key !== " " || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
            return false;
        }

        const quill = this.quillEditor.getQuill();
        const selection = quill.getSelection();
        if (!selection || selection.length > 0) {
            return false;
        }

        const [line, offset] = quill.getLine(selection.index);
        if (!line || offset <= 0) {
            return false;
        }

        const lineStart = selection.index - offset;
        const lineFormat = quill.getFormat(lineStart, 1);
        if (lineFormat.list) {
            return false;
        }

        const prefix = quill.getText(lineStart, offset);
        if (!/^\s*\d+\.$/.test(prefix)) {
            return false;
        }

        event.preventDefault();

        // Эквивалент list-autofill Quill для "1. " -> ordered list.
        quill.insertText(selection.index, " ", "user");
        quill.deleteText(lineStart, offset + 1, "user");
        quill.formatLine(lineStart, 1, "list", "ordered", "user");
        quill.setSelection(lineStart, 0, "silent");

        return true;
    }

    private applyFocusMode(focusAtPointer = false) {
        if (focusAtPointer) {
            const pt = canv.actionManager.getActionPoint();
            const { clientX, clientY } = sceneToClient(pt, canv.m, canv.dpr);
            this.quillEditor.focusAtPoint(clientX, clientY);
            return;
        }

        this.quillEditor.focusToEnd();
    }

    private installClickOutside() {
        const handler = (event: PointerEvent) => {
            const target = event.target as Node | null;
            if (!target) {
                return;
            }

            if (this.quillEditor.contains(target)) {
                return;
            }

            if (
                selectionManager.isOverSide() ||
                selectionManager.isOverCatchers() ||
                selectionManager.isOverRotater()
            ) {
                event.preventDefault();
                return;
            }

            if (canv.appInterface.linkInputController.isInputVisible) {
                return;
            }

            const el = event.target as Element | null;
            if (el && isUIElement(el)) {
                return;
            }

            this.commit();
        };

        document.addEventListener("pointerdown", handler, true);
        this.removeClickOutside = () => {
            document.removeEventListener("pointerdown", handler, true);
        };
    }

    private installEditorContextMenu() {
        const handler = (e: MouseEvent) => {
            const target = e.target as Node | null;
            if (!target || !this.quillEditor.contains(target)) {
                return;
            }

            const canvasRect = canv.canvas.getBoundingClientRect();
            canv.actionManager.lastX = e.clientX - canvasRect.left;
            canv.actionManager.lastY = e.clientY - canvasRect.top;

            e.preventDefault();
            e.stopPropagation();

            canv.contextMenu.show();
            canv.redraw();
        };

        document.addEventListener("contextmenu", handler, true);
        this.removeEditorContextMenu = () => {
            document.removeEventListener("contextmenu", handler, true);
        };
    }

    private cleanup() {
        if (!this.textWidget) {
            return;
        }

        this.lastOverlayTransformSignature = null;

        this.removeClickOutside?.();
        this.removeClickOutside = null;
        this.removeEditorLinkListeners?.();
        this.removeEditorLinkListeners = null;
        this.removeEditorContextMenu?.();
        this.removeEditorContextMenu = null;
        this.clearAutosaveTimer();

        this.textWidget.commitDefferedChanges();
        const shouldPersistEditingState = this.hasUnsavedEditingState();

        this.quillEditor.close();
        canv.appInterface.linkPopupController.detachEditorSession();

        const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");
        const shouldDelegateInteractionStop = !!cursorTool?.isMode?.("interaction");
        const selectedChild = canv.canvasController.selected[0]?.onlyChild?.();
        const isStandaloneTextSelection = selectedChild === this.textWidget;

        if (this.textWidget.getIsEditing()) {
            canv.redraw();
            //Если текстовый виджет никуда не вставлен и mode еще interaction - сохранение произойдет после изменения mode, если нужно
            if (shouldDelegateInteractionStop && isStandaloneTextSelection) {
                if (!shouldPersistEditingState) {
                    this.textWidget.skipNextStopInteractionSave();
                }
            } else {
                // Если текст вставлен в другой виджет-вызываем stopInteraction без сохранения и проверяем стоит ли сохранять изменения виджету
                this.textWidget.stopInteraction(false);

                if (shouldDelegateInteractionStop && !shouldPersistEditingState) {
                    this.textWidget.skipNextHostInteractionSaveCommit();
                }
            }
        }

        const isWidgetSelected = canv.canvasController.selected.length;
        const shouldSyncWidgetAfterEdit =
            this.textWidget.resizeMode === "shape" || this.textWidget.resizeMode === "sticker";

        if (isWidgetSelected && shouldSyncWidgetAfterEdit) {
            const widget = canv.canvasController.selected[0].children[0];

            widget.updateDims();
        }

        // без этого if не будет работать мультивыбор в таблице
        if (
            canv.canvasController.selected.length === 1 &&
            canv.canvasController.selected[0]?.children?.[0] instanceof TableWidget
        ) {
            const table = canv.canvasController.selected[0]?.children?.[0];
            if (table.interactionType === "cellFocus") {
                table.interactionType = "select";
            }
        } else {
            canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
        }

        this.textWidget = null;
        this.lastPersistedEditingStateKey = null;
        this.forcedTextColor = null;

        canv.appInterface.inputFocused = 0;
    }

    private onEditorTextChange = ({ width, height, mode, fixedWidth }: ResizeResult) => {
        if (!this.textWidget) {
            return;
        }

        const prevMode = this.textWidget.resizeMode;

        this.textWidget.setResizeMode(mode);

        if (prevMode === "auto" && mode === "shape" && fixedWidth !== undefined) {
            this.textWidget.hardWidth = fixedWidth;
        }
        this.textWidget.width = width;
        this.textWidget.height = height;

        if (this.textWidget.resizeMode === "sticker") {
            this.alignStickerTextToHost();
        }

        this.textWidget.updateBase(
            this.textWidget.startX,
            this.textWidget.startY - this.textWidget.lineHeight,
            this.textWidget.width,
            this.textWidget.height,
        );

        const selectedWidget = canv.canvasController.selected[0]?.children?.[0];

        if (
            selectedWidget &&
            !(selectedWidget instanceof TextWidgetV2) &&
            !(selectedWidget instanceof StickerWidget) &&
            "TryAction" in selectedWidget
        ) {
            selectedWidget.TryAction("keyboardDown");
        }

        canv.redraw();
        this.updateOverlayTransform();
        canv.appInterface.linkPopupController.onEditorTextChange();
    };

    private onEditorSelectionChange = (summary: SelectionSummary) => {
        if (!this.textWidget) {
            return;
        }

        this.textWidget.setStyleSummary(summary);
        canv.widgetMenu.recollectWidgetOptionsMap();
        canv.appInterface.linkPopupController.onEditorSelectionChange();
    };

    private onEditorContentChange = (meta: TextChangeMeta) => {
        if (!this.textWidget || meta.source !== "user") {
            return;
        }

        this.scheduleAutosave();
    };

    private getPostPasteFormattingTarget() {
        const selectedWidget = canv.canvasController.selected[0]?.children?.[0];

        return selectedWidget instanceof ShapeWidget || selectedWidget instanceof StickerWidget
            ? selectedWidget
            : null;
    }

    private applyPostPasteFormatting() {
        const target = this.getPostPasteFormattingTarget();
        if (!target) return;

        if (target instanceof ShapeWidget) {
            this.applyShapePasteFormatting();
            return;
        }

        this.applyStickerPasteFormatting();
    }

    private getStickerTextColor(): string | null {
        const selectedWidget = canv.canvasController.selected[0]?.children?.[0];
        if (selectedWidget instanceof StickerWidget) {
            return getContrastColor(selectedWidget.color);
        }

        return null;
    }

    private applyStickerPasteFormatting() {
        const color = this.getStickerTextColor();
        if (!color) {
            return;
        }

        this.quillEditor.applyTextFormattingToAll("list", false);
        this.quillEditor.applyTextFormattingToAll("color", color);
    }

    private getShapeTextColor(): string | null {
        const selectedWidget = canv.canvasController.selected[0]?.children?.[0] as ShapeWidget;

        return lightOrDark(selectedWidget.color) === "light" ? "black" : "white";
    }

    private applyShapePasteFormatting() {
        this.quillEditor.applyTextFormattingToAll("list", false);

        const color = this.getShapeTextColor();
        if (!color) {
            return;
        }

        this.quillEditor.applyTextFormattingToAll("color", color);
    }

    private onFontSizeChange = (fontSize: number) => {
        if (!this.textWidget) {
            return;
        }

        this.textWidget.menuTextSize(fontSize);
    };

    private getCurrentTextData = () => {
        if (!this.textWidget) {
            throw new Error("Отсутствует активный textWidget");
        }

        return {
            ...this.textWidget.getData(),
            delta: this.quillEditor.getDelta(),
        };
    };

    private alignStickerTextToHost() {
        if (!this.textWidget || this.textWidget.resizeMode !== "sticker") {
            return;
        }

        const selectedWidget = canv.canvasController.selected[0]?.children?.[0];
        if (!(selectedWidget instanceof StickerWidget)) {
            return;
        }

        if (selectedWidget.textPart !== this.textWidget) {
            return;
        }

        this.textWidget.startX =
            selectedWidget.startX + selectedWidget.width / 2 - this.textWidget.width / 2;
        const textVerticalAlign = this.textWidget.getData().textVerticalAlign;
        if (textVerticalAlign === "top") {
            this.textWidget.startY =
                selectedWidget.startY + this.textWidget.lineHeight + selectedWidget.padding / 2;
            return;
        }

        if (textVerticalAlign === "bottom") {
            this.textWidget.startY =
                selectedWidget.startY +
                selectedWidget.height +
                this.textWidget.lineHeight -
                this.textWidget.height -
                selectedWidget.padding / 2;
            return;
        }

        this.textWidget.startY =
            selectedWidget.startY +
            selectedWidget.height / 2 -
            this.textWidget.height / 2 +
            this.textWidget.lineHeight;
    }

    private scheduleAutosave() {
        this.clearAutosaveTimer();
        this.autosaveTimer = window.setTimeout(() => {
            this.autosaveTimer = null;
            this.persistEditingState();
        }, TEXT_AUTOSAVE_DELAY_MS);
    }

    private clearAutosaveTimer() {
        if (this.autosaveTimer === null) {
            return;
        }

        window.clearTimeout(this.autosaveTimer);
        this.autosaveTimer = null;
    }

    private flushAutosave() {
        this.clearAutosaveTimer();
        this.persistEditingState();
    }

    private hasUnsavedEditingState() {
        if (!this.textWidget) {
            return false;
        }

        return (
            this.buildEditingStateKey(this.normalizeEditingDelta(this.quillEditor.getDelta())) !==
            this.lastPersistedEditingStateKey
        );
    }

    private persistEditingState() {
        if (!this.textWidget) {
            return false;
        }

        if (this.textWidget.aiGenerated) {
            this.textWidget.aiGenerated = false;
        }

        const nextDelta = this.normalizeEditingDelta(this.quillEditor.getDelta());
        const nextStateKey = this.buildEditingStateKey(nextDelta);

        if (nextStateKey === this.lastPersistedEditingStateKey) {
            return false;
        }

        const isPersisted = this.textWidget.persistEditingDraft(nextDelta);

        if (!isPersisted) {
            return false;
        }

        this.lastPersistedEditingStateKey = nextStateKey;
        this.updateOverlayTransform();

        return true;
    }

    private buildEditingStateKey(delta: Delta) {
        if (!this.textWidget) {
            return "";
        }

        const { fontFamily, fontSize, textAlign } = this.textWidget.getData();

        return JSON.stringify({
            startX: this.textWidget.startX,
            startY: this.textWidget.startY,
            resizeMode: this.textWidget.resizeMode,
            hardWidth: this.textWidget.hardWidth,
            aiGenerated: this.textWidget.aiGenerated,
            data: {
                fontFamily,
                fontSize,
                textAlign,
                delta,
            },
        });
    }

    private resolveForcedTextColor(textWidget: TextWidgetV2): string | null {
        switch (textWidget.resizeMode) {
            case "shape": {
                const selectedWidget = canv.canvasController.selected[0]?.children?.[0];

                if (selectedWidget && selectedWidget instanceof ShapeWidget) {
                    return this.getShapeTextColor() ?? textWidget.getCurrentColor();
                }
                return null;
            }
            case "sticker":
                return this.getStickerTextColor() ?? textWidget.getCurrentColor();
            default:
                return null;
        }
    }

    private normalizeEditingDelta(delta: Delta) {
        if (!this.forcedTextColor) {
            return delta;
        }

        return enforceDeltaColor(delta, this.forcedTextColor);
    }

    private installEditorLinkListeners() {
        const root = this.quillEditor.quill.getEditor();
        const controller = canv.appInterface.linkPopupController;

        const resolveLinkElement = (target: EventTarget | null): HTMLAnchorElement | null => {
            if (!(target instanceof Element)) {
                return null;
            }

            return target.closest("a");
        };

        const handlePointerMove = (event: PointerEvent) => {
            const link = resolveLinkElement(event.target);
            if (!link || !this.textWidget) {
                controller.onEditorPointerLinkLeave();
                return;
            }

            controller.onEditorPointerLinkEnterByElement(link);
        };

        const handlePointerLeave = () => {
            controller.onEditorPointerLinkLeave();
        };

        const handlePointerDown = (event: PointerEvent) => {
            const link = resolveLinkElement(event.target);
            if (!link || !this.textWidget) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            controller.onEditorLinkClickByElement(link);
        };

        root.addEventListener("pointermove", handlePointerMove);
        root.addEventListener("pointerleave", handlePointerLeave);
        root.addEventListener("pointerdown", handlePointerDown, true);

        this.removeEditorLinkListeners = () => {
            root.removeEventListener("pointermove", handlePointerMove);
            root.removeEventListener("pointerleave", handlePointerLeave);
            root.removeEventListener("pointerdown", handlePointerDown, true);
        };
    }

    private syncResizeOptionsFromWidget() {
        if (!this.textWidget) {
            return;
        }

        const { fontSize } = this.textWidget.getData();

        this.quillEditor.setResizeOptions({
            mode: this.textWidget.resizeMode,
            emptyWidth: this.textWidget.getEmptyInputWidth(),
            paddingX: this.textWidget.horizontalPadding,
            fixedWidth: this.textWidget.hardWidth,
            fixedHeight: this.textWidget.hardHeight,
            currentFontSize: fontSize,
            onlyLineMode: this.textWidget.onlyLineMode,
        });
    }

    syncEditorAfterStyleChange() {
        if (!this.textWidget) {
            return;
        }

        this.syncResizeOptionsFromWidget();
        this.quillEditor.remeasure();
        if (this.textWidget.resizeMode === "sticker") {
            this.quillEditor.flushStickerFit();
        }
        this.updateOverlayTransform();
    }

    getIsTextChanged() {
        return this.quillEditor.getIsTextChanged();
    }
}
